import {
  Component,
  computed,
  effect,
  inject,
  signal,
  untracked,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule } from '@ngx-translate/core';
import { LANGUAGES } from '@core/constants';
import { getUserActivePartner, getUserRole, UserProfile } from '@core/models';
import { UserStore } from '@root/app/store/user.store';
import { environment } from '@root/environments/environment';
import { PageBodyComponent, PageHeaderComponent } from '@custom-components';
import { CardComponent } from '@custom-components/general/card/card.component';
import { CardGridComponent } from '@custom-components/general/card-grid';
import { DetailFieldComponent } from '@custom-components/general/detail-field';
import { EditProfileDialogComponent } from '../../components/edit-profile-dialog/edit-profile-dialog.component';
import { ChangePasswordDialogComponent } from '../../components/change-password-dialog/change-password-dialog.component';

@Component({
  selector: 'xemex-profile-page',
  imports: [
    TranslateModule,
    PageHeaderComponent,
    PageBodyComponent,
    CardGridComponent,
    CardComponent,
    DetailFieldComponent,
  ],
  templateUrl: './profile-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './profile-page.component.scss',
})
export class ProfilePageComponent {
  readonly userStore = inject(UserStore);
  private readonly dialog = inject(MatDialog);

  userProfile: UserProfile | undefined;
  oid = '';

  readonly LANGUAGES = LANGUAGES;

  /** Bumped whenever the stored avatar changes, to cache-bust the header image URL below. */
  private readonly avatarVersion = signal(0);

  /** Avatar endpoint URL, fetched authenticated by the page-header's xemexFetchImage (like the
   * device image). Only set when the store resolved a real avatar — otherwise the header falls
   * back to the person icon. The version query param forces a re-fetch after an upload, since the
   * URL is otherwise identical and would be served from the image cache. */
  readonly avatarUrl = computed(() => {
    const oid = this.userStore.user()?.object_id;
    return oid && this.userStore.userAvatar()
      ? `${environment.api.url}/users/${oid}/avatar?v=${this.avatarVersion()}`
      : undefined;
  });

  constructor() {
    effect(() => {
      this.userProfile = this.userStore.user();
      this.oid = this.userProfile.object_id;
    });

    // When the store swaps in a new avatar (e.g. after an upload completes), advance the
    // cache-bust token so the page-header refetches instead of showing the cached image.
    effect(() => {
      this.userStore.userAvatar();
      untracked(() => this.avatarVersion.update(v => v + 1));
    });
  }

  get companyName(): string {
    return getUserActivePartner(this.userProfile)?.partnerName ?? '';
  }

  get roleNameKey(): string {
    const role = this.userProfile ? getUserRole(this.userProfile) : undefined;
    return role?.roleName ? `roles.roleNames.${role.roleName.toLowerCase()}` : '';
  }

  get languageLabel(): string {
    const code = this.userProfile?.language;
    return LANGUAGES.find(l => l.code.toUpperCase() === code?.toUpperCase())?.translationCode ?? '';
  }

  onEditProfile() {
    this.dialog
      .open(EditProfileDialogComponent, {
        width: '48rem',
        maxWidth: 'calc(100vw - 2rem)',
        maxHeight: 'calc(100vh - 2rem)',
        data: { userProfile: this.userProfile, avatar: this.userStore.userAvatar() },
      })
      .afterClosed()
      .subscribe(result => {
        if (!result) return;
        this.userStore.updateUserMethod({ user: result.user, oid: this.oid });
        if (result.avatar) {
          this.userStore.updateUserAvatarMethod({ avatar: result.avatar, oid: this.oid });
        }
      });
  }

  onChangePassword() {
    this.dialog.open(ChangePasswordDialogComponent, {
      width: '48rem',
      maxWidth: 'calc(100vw - 2rem)',
      maxHeight: 'calc(100vh - 2rem)',
      data: { userProfile: this.userProfile },
    });
  }
}
